import { Head, useForm } from "@inertiajs/react"
import { Button, Checkbox, Flex, Heading, Tabs, Text } from "@radix-ui/themes"
import { z } from "zod"

import FileUpload from "@/components/file-upload"
import InputError from "@/components/input-error"
import ProjectLayout from "@/layouts/project-layout"
import { projectDashboardPath, projectSettingsPath } from "@/routes"
import { ProjectSchema } from "@/schemas"

const PropsSchema = z.object({
  project: ProjectSchema,
  apns_key_file_attached: z.boolean(),
})

type Props = z.infer<typeof PropsSchema>

export default function SettingsShow(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { project, apns_key_file_attached } = validatedProps

  const { data, setData, patch, processing, errors } = useForm({
    name: project.name || "",
    description: project.description ?? "",
    postmark_api_token: project.postmark_api_token ?? "",
    sender_name: project.sender_name ?? "",
    sender_email: project.sender_email ?? "",
    apns_team_id: project.apns_team_id ?? "",
    apns_key_id: project.apns_key_id ?? "",
    apns_bundle_id: project.apns_bundle_id ?? "",
    apns_key_file: null as File | null,
    remove_apns_key_file: false as boolean,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(projectSettingsPath(project.slug))
  }

  const handleFileSelect = (file: File | null) => {
    setData("apns_key_file", file)
    if (file) {
      setData("remove_apns_key_file", false)
    }
  }

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Settings" },
      ]}
    >
      <Head title="Project Settings" />

      <Flex direction="column" gap="6" p="6">
        <Flex direction="column" gap="1">
          <Heading size="6" weight="medium">
            Project Settings
          </Heading>
          <Text color="gray" size="2">
            Manage your project configuration and integrations
          </Text>
        </Flex>

        <form onSubmit={handleSubmit} className="max-w-4xl">
          <Tabs.Root defaultValue="general">
            <Tabs.List>
              <Tabs.Trigger value="general">General</Tabs.Trigger>
              <Tabs.Trigger value="email">Email</Tabs.Trigger>
              <Tabs.Trigger value="push">Push</Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="general" className="pt-6">
              <Flex direction="column" gap="5">
                <div className="flex items-start gap-6">
                  <div className="w-48 flex-shrink-0">
                    <label htmlFor="name" className="block pt-2 text-sm font-medium text-gray-900">
                      Project Name <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="flex-1">
                    <input
                      id="name"
                      type="text"
                      value={data.name}
                      onChange={(e) => setData("name", e.target.value)}
                      placeholder="My Project"
                      required
                      className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <InputError message={errors.name} />
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="w-48 flex-shrink-0">
                    <label
                      htmlFor="description"
                      className="block pt-2 text-sm font-medium text-gray-900"
                    >
                      Description
                    </label>
                  </div>
                  <div className="flex-1">
                    <textarea
                      id="description"
                      value={data.description}
                      onChange={(e) => setData("description", e.target.value)}
                      placeholder="A brief description of your project"
                      rows={3}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <InputError message={errors.description} />
                  </div>
                </div>
              </Flex>
            </Tabs.Content>

            <Tabs.Content value="email" className="pt-6">
              <Flex direction="column" gap="5">
                <div className="flex items-start gap-6">
                  <div className="w-48 flex-shrink-0">
                    <label
                      htmlFor="postmark_api_token"
                      className="block pt-2 text-sm font-medium text-gray-900"
                    >
                      Postmark API Token
                    </label>
                  </div>
                  <div className="flex-1">
                    <input
                      id="postmark_api_token"
                      type="text"
                      value={data.postmark_api_token}
                      onChange={(e) => setData("postmark_api_token", e.target.value)}
                      placeholder="your-postmark-api-token"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <InputError message={errors.postmark_api_token} />
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="w-48 flex-shrink-0">
                    <label
                      htmlFor="sender_name"
                      className="block pt-2 text-sm font-medium text-gray-900"
                    >
                      Sender Name
                    </label>
                  </div>
                  <div className="flex-1">
                    <input
                      id="sender_name"
                      type="text"
                      value={data.sender_name}
                      onChange={(e) => setData("sender_name", e.target.value)}
                      placeholder="Your Company Name"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <InputError message={errors.sender_name} />
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="w-48 flex-shrink-0">
                    <label
                      htmlFor="sender_email"
                      className="block pt-2 text-sm font-medium text-gray-900"
                    >
                      Sender Email <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="flex-1">
                    <input
                      id="sender_email"
                      type="email"
                      value={data.sender_email}
                      onChange={(e) => setData("sender_email", e.target.value)}
                      placeholder="noreply@yourcompany.com"
                      required
                      className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <InputError message={errors.sender_email} />
                  </div>
                </div>
              </Flex>
            </Tabs.Content>

            <Tabs.Content value="push" className="pt-6">
              <Flex direction="column" gap="5">
                <div className="flex items-start gap-6">
                  <div className="w-48 flex-shrink-0">
                    <label
                      htmlFor="apns_team_id"
                      className="block pt-2 text-sm font-medium text-gray-900"
                    >
                      APNs Team ID
                    </label>
                  </div>
                  <div className="flex-1">
                    <input
                      id="apns_team_id"
                      type="text"
                      value={data.apns_team_id}
                      onChange={(e) => setData("apns_team_id", e.target.value)}
                      placeholder="Team ID"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <InputError message={errors.apns_team_id} />
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="w-48 flex-shrink-0">
                    <label
                      htmlFor="apns_key_id"
                      className="block pt-2 text-sm font-medium text-gray-900"
                    >
                      APNs Key ID
                    </label>
                  </div>
                  <div className="flex-1">
                    <input
                      id="apns_key_id"
                      type="text"
                      value={data.apns_key_id}
                      onChange={(e) => setData("apns_key_id", e.target.value)}
                      placeholder="Key ID"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <InputError message={errors.apns_key_id} />
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="w-48 flex-shrink-0">
                    <label
                      htmlFor="apns_bundle_id"
                      className="block pt-2 text-sm font-medium text-gray-900"
                    >
                      APNs Bundle ID
                    </label>
                  </div>
                  <div className="flex-1">
                    <input
                      id="apns_bundle_id"
                      type="text"
                      value={data.apns_bundle_id}
                      onChange={(e) => setData("apns_bundle_id", e.target.value)}
                      placeholder="com.yourcompany.app"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <InputError message={errors.apns_bundle_id} />
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="w-48 flex-shrink-0">
                    <span className="block pt-2 text-sm font-medium text-gray-900">
                      APNs Key File
                    </span>
                  </div>
                  <div className="flex-1">
                    <FileUpload
                      file={data.apns_key_file}
                      onFileSelect={handleFileSelect}
                      accept=".p8"
                      label=".p8 file"
                      description="Upload your APNs auth key"
                      error={errors.apns_key_file}
                      disabled={processing}
                    />
                    <Text size="2" color="gray" className="mt-2">
                      {apns_key_file_attached ? "Key file uploaded" : "No key file uploaded"}
                    </Text>

                    {apns_key_file_attached && (
                      <Flex align="center" gap="2" mt="3">
                        <Checkbox
                          checked={data.remove_apns_key_file}
                          onCheckedChange={(checked) =>
                            setData("remove_apns_key_file", checked === true)
                          }
                        />
                        <Text size="2">Remove key file</Text>
                      </Flex>
                    )}
                  </div>
                </div>
              </Flex>
            </Tabs.Content>
          </Tabs.Root>

          <Flex gap="3" justify="end" mt="6">
            <Button type="submit" disabled={processing} variant="classic">
              {processing ? "Saving..." : "Save Changes"}
            </Button>
          </Flex>
        </form>
      </Flex>
    </ProjectLayout>
  )
}
